import { Link, useNavigate, useParams, useRouterState } from '@tanstack/react-router';
import { PhoneOutgoing, Plus } from 'lucide-react';
import { useState } from 'react';
import { CALL_OUTCOMES, CALL_OUTCOME_LABELS } from '@salesbee/contracts';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogContent,
  Drawer,
  DrawerContent,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  cn,
  formatDateTime,
  segmentedItemClass,
  segmentedListClass,
  useToast,
} from '@salesbee/ui';
import { FeatureGate, OutcomeBadge, QueryView, Section, Stat } from '../components/common';
import { errorMessage, trpc } from '../lib/trpc';

function OutboundTabs() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  return (
    <div className={cn(segmentedListClass, 'mb-6')}>
      <Link to="/outbound/campaigns" className={segmentedItemClass(pathname.startsWith('/outbound/campaigns'))}>Campaigns</Link>
      <Link to="/outbound/calls" className={segmentedItemClass(pathname.startsWith('/outbound/calls'))}>Call log</Link>
    </div>
  );
}

function ProviderBanner() {
  const p = trpc.outbound.providers.useQuery();
  if (!p.data?.simulated) return null;
  return <Alert variant="info" className="mb-6">Voice provider: stub (simulated calls with deterministic outcomes). Set VOICE_PROVIDER=elevenlabs and its keys to place real calls.</Alert>;
}

const STATUS_VARIANT: Record<string, 'default' | 'success' | 'warning' | 'info' | 'outline'> = { draft: 'outline', active: 'success', paused: 'warning', completed: 'info', archived: 'default' };

function NewCampaignDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const lists = trpc.crm.lists.list.useQuery();
  const create = trpc.outbound.campaigns.create.useMutation();
  const navigate = useNavigate();
  const [f, setF] = useState({ name: '', goal: '', script: '', mode: 'ai' as 'ai' | 'human', contactListId: '', maxAttempts: '3', start: '09:30', end: '17:00', autoDial: false });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF((x) => ({ ...x, [k]: e.target.value }));
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="New campaign" size="lg">
        <div className="space-y-4">
          {create.error ? <Alert variant="destructive">{errorMessage(create.error)}</Alert> : null}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Name"><Input value={f.name} onChange={set('name')} /></Field>
            <Field label="Audience (list or segment)"><Select value={f.contactListId} onChange={set('contactListId')} placeholder="Choose a list" options={(lists.data ?? []).map((l) => ({ value: l.id, label: `${l.name} (${l.size})` }))} /></Field>
            <Field label="Mode"><Select value={f.mode} onChange={set('mode')} options={[{ value: 'ai', label: 'AI voice agent' }, { value: 'human', label: 'Human (click-to-call)' }]} /></Field>
            <Field label="Max attempts per contact"><Select value={f.maxAttempts} onChange={set('maxAttempts')} options={['1', '2', '3', '4', '5'].map((v) => ({ value: v, label: v }))} /></Field>
            <Field label="Call from (callee local time)"><Input type="time" value={f.start} onChange={set('start')} /></Field>
            <Field label="until"><Input type="time" value={f.end} onChange={set('end')} /></Field>
          </div>
          <Field label="Goal"><Input value={f.goal} onChange={set('goal')} placeholder="Book an on-site inspection" /></Field>
          <Field label="Script for the AI agent (after the disclosure)"><Textarea rows={4} value={f.script} onChange={set('script')} /></Field>
          <Checkbox label="Auto-dial (paced by the cron every minute)" checked={f.autoDial} onCheckedChange={(v) => setF((x) => ({ ...x, autoDial: v === true }))} />
          <Button
            disabled={!f.name.trim() || !f.contactListId}
            loading={create.isPending}
            onClick={() =>
              create.mutate(
                { name: f.name, goal: f.goal || null, script: f.script, mode: f.mode, contactListId: f.contactListId, maxAttempts: Number(f.maxAttempts), autoDial: f.autoDial, schedule: { days: [1, 2, 3, 4, 5], window: { start: f.start, end: f.end } } },
                { onSuccess: (c) => { onOpenChange(false); void navigate({ to: '/outbound/campaigns/$campaignId', params: { campaignId: c.id } }); } },
              )
            }
          >
            Create campaign (draft)
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function CampaignsPage() {
  return (
    <FeatureGate feature="outbound_calling">
      <Campaigns />
    </FeatureGate>
  );
}

function Campaigns() {
  const list = trpc.outbound.campaigns.list.useQuery();
  const [open, setOpen] = useState(false);
  return (
    <div>
      <PageHeader title="Outbound" description="AI calling campaigns with the compliance gate on every dial." actions={<Button onClick={() => setOpen(true)}><Plus /> New campaign</Button>} />
      <OutboundTabs />
      <ProviderBanner />
      <QueryView query={list}>
        {(data) =>
          data.length === 0 ? (
            <EmptyState icon={<PhoneOutgoing />} title="No campaigns yet" description="Create a campaign from a list or segment." />
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {data.map((c) => (
                <Link key={c.id} to="/outbound/campaigns/$campaignId" params={{ campaignId: c.id }}>
                  <Card className="transition-shadow hover:shadow-pop">
                    <CardContent className="space-y-3 py-5">
                      <div className="flex items-center justify-between">
                        <h3 className="text-[16px] font-semibold">{c.name}</h3>
                        <Badge variant={STATUS_VARIANT[c.status]}>{c.status}</Badge>
                      </div>
                      <p className="text-[14px] text-muted-foreground">{c.goal ?? (c.mode === 'ai' ? 'AI voice campaign' : 'Human calling campaign')}</p>
                      <div className="flex gap-4 font-mono text-[13px]">
                        <span>{c.stats.totalCalls} calls</span>
                        <span>{Math.round(c.stats.connectRate * 100)}% connect</span>
                        <span>{c.stats.meetings} meetings</span>
                        <span>{c.stats.targets.queued ?? 0} queued</span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )
        }
      </QueryView>
      <NewCampaignDialog open={open} onOpenChange={setOpen} />
    </div>
  );
}

export function CampaignDetailPage() {
  const { campaignId } = useParams({ strict: false }) as { campaignId: string };
  const c = trpc.outbound.campaigns.get.useQuery({ id: campaignId });
  const setStatus = trpc.outbound.campaigns.setStatus.useMutation();
  const dial = trpc.outbound.campaigns.dialNext.useMutation();
  const utils = trpc.useUtils();
  const { success, error, toast } = useToast();
  const refresh = () => Promise.all([utils.outbound.campaigns.get.invalidate(), utils.outbound.campaigns.list.invalidate()]);
  const changeStatus = (status: 'active' | 'paused' | 'completed' | 'archived') =>
    setStatus.mutate({ id: campaignId, status }, { onSuccess: async (r) => { success(`Campaign ${status}`, r.targetsAdded ? `${r.targetsAdded} contacts queued` : undefined); await refresh(); }, onError: (e) => error('Could not change status', errorMessage(e)) });
  return (
    <FeatureGate feature="outbound_calling">
      <OutboundTabs />
      <ProviderBanner />
      <QueryView query={c}>
        {(camp) => (
          <div className="space-y-6">
            <PageHeader
              title={camp.name}
              description={camp.goal ?? undefined}
              actions={
                <>
                  <Badge variant={STATUS_VARIANT[camp.status]}>{camp.status}</Badge>
                  {camp.status === 'draft' || camp.status === 'paused' ? <Button variant="outline" onClick={() => changeStatus('active')} loading={setStatus.isPending}>Activate</Button> : null}
                  {camp.status === 'active' ? <Button variant="outline" onClick={() => changeStatus('paused')}>Pause</Button> : null}
                  <Button
                    disabled={camp.status !== 'active'}
                    loading={dial.isPending}
                    onClick={() =>
                      dial.mutate(
                        { id: campaignId },
                        {
                          onSuccess: async (r) => {
                            if (r.dialed) success('Call placed', r.outcome ? `Outcome: ${CALL_OUTCOME_LABELS[r.outcome as keyof typeof CALL_OUTCOME_LABELS] ?? r.outcome}` : 'Waiting for the provider');
                            else toast({ title: 'Nothing dialed', description: r.message, variant: r.reason === 'workspace_blocked' || r.reason === 'minutes_exhausted' ? 'error' : 'warning' });
                            await refresh();
                          },
                          onError: (e) => error('Dial failed', errorMessage(e)),
                        },
                      )
                    }
                  >
                    <PhoneOutgoing /> Dial next
                  </Button>
                </>
              }
            />
            <div className="grid gap-4 sm:grid-cols-4">
              <Stat label="Calls" value={camp.stats.totalCalls} />
              <Stat label="Connect rate" value={`${Math.round(camp.stats.connectRate * 100)}%`} />
              <Stat label="Meetings" value={camp.stats.meetings} />
              <Stat label="Minutes" value={camp.stats.minutes} />
            </div>
            <Section title="Dialer queue" description={`Max ${camp.maxAttempts} attempts, retry after ${Math.round(camp.retryDelayMinutes / 60)} h, ${camp.schedule.window.start}-${camp.schedule.window.end} callee local time, weekdays.`}>
              <Card>
                <Table>
                  <TableHeader>
                    <TableRow><TableHead>Contact</TableHead><TableHead>Status</TableHead><TableHead>Attempts</TableHead><TableHead>Last outcome</TableHead><TableHead>Next attempt</TableHead><TableHead>Compliance</TableHead></TableRow>
                  </TableHeader>
                  <TableBody>
                    {camp.targets.map((t) => (
                      <TableRow key={t.id}>
                        <TableCell><Link className="font-medium hover:underline" to="/crm/contacts/$contactId" params={{ contactId: t.contactId }}>{t.name}</Link></TableCell>
                        <TableCell><Badge variant={t.status === 'dnc' ? 'destructive' : t.status === 'queued' ? 'outline' : 'default'}>{t.status}</Badge></TableCell>
                        <TableCell className="font-mono">{t.attempts}</TableCell>
                        <TableCell>{t.lastOutcome ? <OutcomeBadge outcome={t.lastOutcome} /> : '—'}</TableCell>
                        <TableCell className="text-[13px]">{t.nextAttemptAt ? formatDateTime(t.nextAttemptAt) : '—'}</TableCell>
                        <TableCell className="text-[12px] text-muted-foreground">{t.skipReason?.replace(/_/g, ' ') ?? ''}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {camp.targets.length === 0 ? <p className="p-6 text-center text-sm text-muted-foreground">Activate the campaign to queue its audience.</p> : null}
              </Card>
            </Section>
            <Section title="Recent calls">
              <CallsTable calls={camp.calls.map((x) => ({ ...x, contactName: camp.targets.find((t) => t.contactId === x.contactId)?.name ?? null, campaignName: camp.name }))} />
            </Section>
          </div>
        )}
      </QueryView>
    </FeatureGate>
  );
}

type CallRow = { id: string; createdAt: Date; mode: string; outcome: string | null; durationSeconds: number; summary: string | null; contactName: string | null; campaignName: string | null; transcript: { speaker: string; text: string }[]; disclosureText: string | null; status: string };

function CallsTable({ calls }: { calls: CallRow[] }) {
  const [open, setOpen] = useState<CallRow | null>(null);
  const setOutcome = trpc.outbound.calls.setOutcome.useMutation();
  const utils = trpc.useUtils();
  return (
    <Card>
      <Table>
        <TableHeader>
          <TableRow><TableHead>When</TableHead><TableHead>Contact</TableHead><TableHead>Campaign</TableHead><TableHead>Mode</TableHead><TableHead>Duration</TableHead><TableHead>Outcome</TableHead></TableRow>
        </TableHeader>
        <TableBody>
          {calls.map((c) => (
            <TableRow key={c.id} className="cursor-pointer hover:bg-muted/50" onClick={() => setOpen(c)}>
              <TableCell className="text-[13px]">{formatDateTime(c.createdAt)}</TableCell>
              <TableCell className="font-medium">{c.contactName ?? '—'}</TableCell>
              <TableCell>{c.campaignName ?? '—'}</TableCell>
              <TableCell>{c.mode === 'ai' ? 'AI' : 'Human'}</TableCell>
              <TableCell className="font-mono text-[13px]">{Math.floor(c.durationSeconds / 60)}:{String(c.durationSeconds % 60).padStart(2, '0')}</TableCell>
              <TableCell><OutcomeBadge outcome={c.outcome} /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {calls.length === 0 ? <p className="p-6 text-center text-sm text-muted-foreground">No calls yet.</p> : null}
      <Drawer open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DrawerContent title={open?.contactName ?? 'Call'} description={open ? formatDateTime(open.createdAt) : undefined}>
          {open ? (
            <div className="space-y-4 text-[14px]">
              <OutcomeBadge outcome={open.outcome} />
              {open.summary ? <p>{open.summary}</p> : null}
              {open.disclosureText ? <Alert variant="info" title="Disclosure spoken">{open.disclosureText}</Alert> : null}
              <div className="space-y-2">
                <h3 className="font-semibold">Transcript</h3>
                {open.transcript.length === 0 ? <p className="text-muted-foreground">No transcript.</p> : null}
                {open.transcript.map((t, i) => (
                  <p key={i}><span className="font-medium">{t.speaker === 'agent' ? 'Agent' : 'Contact'}:</span> {t.text}</p>
                ))}
              </div>
              <Field label="Correct the outcome">
                <Select
                  value={open.outcome ?? ''}
                  onChange={(e) => setOutcome.mutate({ callId: open.id, outcome: e.target.value as 'connected' }, { onSuccess: () => { setOpen({ ...open, outcome: e.target.value }); void utils.outbound.invalidate(); } })}
                  options={CALL_OUTCOMES.map((o) => ({ value: o, label: CALL_OUTCOME_LABELS[o] }))}
                />
              </Field>
            </div>
          ) : null}
        </DrawerContent>
      </Drawer>
    </Card>
  );
}

export function CallsPage() {
  const [outcome, setOutcome] = useState('');
  const calls = trpc.outbound.calls.list.useQuery({ outcome: (outcome || undefined) as 'connected' | undefined, limit: 200 });
  return (
    <FeatureGate feature="outbound_calling">
      <PageHeader title="Outbound" description="Every call with its outcome, summary and transcript." />
      <OutboundTabs />
      <Select className="mb-4 w-56" value={outcome} onChange={(e) => setOutcome(e.target.value)} placeholder="All outcomes" options={CALL_OUTCOMES.map((o) => ({ value: o, label: CALL_OUTCOME_LABELS[o] }))} />
      <QueryView query={calls}>{(data) => <CallsTable calls={data.items} />}</QueryView>
    </FeatureGate>
  );
}
